import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import CategorySidebar from '@/components/library/CategorySidebar'
import BookCard from '@/components/library/BookCard'
import Input from '@/components/ui/Input'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { libraryApi } from '@/services/libraryApiService'
import { mockBooks, mockCategories } from '@/services/mockDataService'

// Helper to normalize book data
const normalizeBook = (book: any): any => ({
  id: book.id || book.Id || '',
  title: book.title || book.Title || book.name || book.Name || '',
  author: book.author || book.Author || book.authorName || book.writer || '',
  coverImageUrl: book.coverImageUrl || book.CoverImageUrl || book.imageUrl || book.ImageUrl || book.thumbnailUrl || '',
  categoryId: book.categoryId || book.CategoryId || '',
  categoryName: book.categoryName || book.CategoryName || '',
  isActive: book.isActive ?? book.IsActive ?? true,
})

// Helper to normalize category data
const normalizeCategory = (c: any): any => ({
  id: c.id || c.Id || '',
  name: c.name || c.Name || '',
  nameEn: c.nameEn || c.NameEn || '',
  order: c.order ?? c.Order ?? 0,
})

export default function LibraryIndexPage() {
  const { t, i18n } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [books, setBooks] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch categories when language changes
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await libraryApi.getCategories()
        if (data) {
          setCategories((data || []).map(normalizeCategory))
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err)
        setError('فشل تحميل قائمة التصنيفات')
        setCategories(mockCategories.map((c) => normalizeCategory({
          id: c.id.toString(),
          name: c.name,
          nameEn: c.nameEn,
          order: c.order || 0,
        })))
      } finally {
        setIsLoading(false)
      }
    }
    fetchCategories()
  }, [i18n.language])

  // Fetch books when category, search or language changes
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await libraryApi.getBooks(selectedCategory || undefined, searchQuery || undefined)
        if (data) {
          setBooks((data || []).map(normalizeBook))
        }
      } catch (err) {
        console.error('Failed to fetch books:', err)
        setError('فشل تحميل قائمة الكتب')
        setBooks(mockBooks.map((b) => normalizeBook({
          id: b.id.toString(),
          title: b.title,
          author: b.author,
          description: b.description,
          categoryId: b.category.toString(),
          categoryName: b.categoryName,
          coverImageUrl: b.coverImage,
          order: 0,
        })))
      } finally {
        setIsLoading(false)
      }
    }
    fetchBooks()
  }, [selectedCategory, searchQuery, i18n.language])

  const filteredBooks = books

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.library') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('common.library')} - سكينة</title>
        <meta name="description" content={t('public.library.seo.description')} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('common.library')}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">{t('public.library.intro')}</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('public.library.searchPlaceholder')}
              className="pr-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <CategorySidebar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={setSelectedCategory}
            />
          </div>

          {/* Books Grid */}
          <div className="lg:col-span-3">
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-400">{t('public.library.resultsCount', { count: filteredBooks.length })}</p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : filteredBooks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredBooks.map((book) => (
                  <BookCard key={book.id} book={book} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-400">{t('public.library.noResults')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
